/**
 * Calculate 5-Year Summary totals from years 0-4.
 * Pure function: accepts all data and helpers via deps; no refs/state inside.
 */
import {
  calculate05PercentIFF,
  calculate10PercentPV,
  calculate3PercentWAC,
  calculateAnnualBenefit,
  calculateGPPerUnit,
  calculateGrossProfit,
  calculateNet,
  calculateTransferPrice,
  calculateUnitBenefit,
} from '../../bidAnalysisFormulas';
import { deriveContractRevenueAndGPForDisplay } from '../../bidAnalysisTemplate.logic';

export interface Calculate5YearSummaryDeps {
  copyAllYearsData: Record<number, Record<number, any>> | null | undefined;
  yearFormDataRefCurrent: Record<number, Record<number, any>> | null | undefined;
  yearFormData: Record<number, Record<number, any>>;
  templateType: string | null | undefined;
  allPackSizeTabs: Array<{ index: number }>;
  activePackSizeTab: number | null;
  supplierName: string;
  formData: Record<string, any>;
  packSizeFormData: Record<number, Record<string, any>>;
}

export interface FiveYearSummaryResult {
  totalRevenue: number;
  totalGP: number;
  totalGrossProfit: number;
  totalGpPerUnit: number;
  totalWAC: number;
  totalAnnualBenefit: number;
  yearData: Array<{
    year: string;
    revenue: number;
    gp: number;
    grossProfit: number;
    gpPerUnit: number;
    supplierName: string;
    supplierBulkNdc: string;
    gsmsNdc: string;
    annualBenefit: number;
  }>;
}

export function calculate5YearSummary(deps: Calculate5YearSummaryDeps): FiveYearSummaryResult {
  const {
    copyAllYearsData,
    yearFormDataRefCurrent,
    yearFormData,
    templateType,
    allPackSizeTabs,
    activePackSizeTab,
    supplierName,
    formData,
    packSizeFormData,
  } = deps;

  const copyData = copyAllYearsData;
  const refData = yearFormDataRefCurrent;
  const stateData = yearFormData;
  const allTabKeys = new Set([
    ...Object.keys(copyData || {}),
    ...Object.keys(refData || {}),
    ...Object.keys(stateData || {}),
  ]);
  const latestYearFormData: Record<number, Record<number, any>> = {};
  allTabKeys.forEach(tabStr => {
    const tab = parseInt(tabStr, 10);
    if (isNaN(tab)) return;
    latestYearFormData[tab] = {};
    for (let yearIndex = 0; yearIndex <= 4; yearIndex++) {
      const copyYear = copyData?.[tab]?.[yearIndex];
      const stateYear = stateData?.[tab]?.[yearIndex];
      const refYear = refData?.[tab]?.[yearIndex];
      const copyHasData = copyYear && typeof copyYear === 'object' && Object.keys(copyYear).length > 0;
      const stateHasData = stateYear && typeof stateYear === 'object' && Object.keys(stateYear).length > 0;
      const refHasData = refYear && typeof refYear === 'object' && Object.keys(refYear).length > 0;
      if (yearIndex >= 1 && copyHasData) {
        latestYearFormData[tab][yearIndex] = stateHasData ? stateYear : copyYear;
      } else {
        latestYearFormData[tab][yearIndex] =
          stateHasData ? stateYear : refHasData ? refYear : (stateYear ?? refYear ?? {});
      }
    }
  });

  const isNC = templateType?.toLowerCase() === 'nc';
  const tabsToUse = isNC
    ? allPackSizeTabs
    : activePackSizeTab !== null
      ? [{ index: activePackSizeTab }]
      : [];

  const supplierNameValue = supplierName || formData.supplierName || '';
  let totalRevenue = 0;
  let totalGP = 0;
  let totalAnnualUsage = 0;
  let totalWAC = 0;
  let totalAnnualBenefit = 0;

  const yearData: Array<{
    year: string;
    revenue: number;
    gp: number;
    grossProfit: number;
    gpPerUnit: number;
    supplierName: string;
    supplierBulkNdc: string;
    gsmsNdc: string;
    annualBenefit: number;
  }> = [];

  for (let yearIndex = 0; yearIndex <= 4; yearIndex++) {
    let yearRevenue = 0;
    let yearGP = 0;
    let yearAnnualUsage = 0;
    const yearLabel = yearIndex === 0 ? 'Base Year' : `Option Year ${yearIndex}`;

    tabsToUse.forEach(tab => {
      const tabIndex = tab.index;
      const basePackData = packSizeFormData[tabIndex] || {};
      const yearSpecificData = latestYearFormData[tabIndex]?.[yearIndex] || {};
      const mergedData = { ...basePackData, ...yearSpecificData };
      const yearDataEntry = deriveContractRevenueAndGPForDisplay(mergedData);
      const revenue = parseFloat(yearDataEntry.contractAnnualRevenue || '0') || 0;
      const gp = parseFloat(yearDataEntry.contractAnnualGP || '0') || 0;
      const mergedForUsage = { ...basePackData, ...yearSpecificData };
      const annualUsage = parseFloat(mergedForUsage.ncEstAnnualUsage || mergedForUsage.targetAnnualUsage || '0') || 0;

      if (!isNaN(revenue)) yearRevenue += revenue;
      if (!isNaN(gp)) yearGP += gp;
      if (!isNaN(annualUsage)) yearAnnualUsage += annualUsage;
    });

    const grossProfit = calculateGrossProfit(yearRevenue, yearGP);
    const gpPerUnit = calculateGPPerUnit(yearGP, yearAnnualUsage);

    const firstTabIndex =
      tabsToUse.length > 0
        ? tabsToUse[0].index
        : activePackSizeTab !== null
          ? activePackSizeTab
          : 0;
    const firstYearDataEntry = latestYearFormData[firstTabIndex]?.[yearIndex] || {};
    const supplierBulkNdc =
      firstYearDataEntry.selectedMfgNdc ||
      firstYearDataEntry.mfg_ndc ||
      firstYearDataEntry.gsms_mfg_ndc ||
      '';
    const gsmsNdc = firstYearDataEntry.gsms_ndc || '';

    totalRevenue += yearRevenue;
    totalGP += yearGP;
    totalAnnualUsage += yearAnnualUsage;

    let yearWAC = 0;
    let yearAnnualBenefit = 0;

    tabsToUse.forEach(tab => {
      const tabIndex = tab.index;
      const yearDataEntry = latestYearFormData[tabIndex]?.[yearIndex] || {};
      const baseData = packSizeFormData[tabIndex] || {};
      const wac = parseFloat(yearDataEntry.wac || '0') || 0;
      const bidPrice = parseFloat(yearDataEntry.suggestedBidPrice || '0') || 0;
      const gsmsNetCostFromMfg = parseFloat(yearDataEntry.gsmsNetCostFromMfg || '0') || 0;
      const annualUsage =
        parseFloat(yearDataEntry.ncEstAnnualUsage || yearDataEntry.targetAnnualUsage || '0') || 0;
      const mfgBottleSize = parseFloat(baseData?.mfgBottleSize || baseData?.packSize || '0') || 0;
      const gsmsPackSize = parseFloat(baseData?.gsmsPackSize || baseData?.packSize || '0') || 0;

      if (wac > 0 && bidPrice > 0) {
        yearWAC += wac;

        const wac3Percent = calculate3PercentWAC(wac);
        const pv10Percent = calculate10PercentPV(bidPrice);
        const iff05Percent = calculate05PercentIFF(bidPrice);
        const net = calculateNet(bidPrice, wac3Percent, pv10Percent, iff05Percent);
        const transferPrice = calculateTransferPrice(
          gsmsNetCostFromMfg,
          mfgBottleSize,
          gsmsPackSize
        );
        const unitBenefit = calculateUnitBenefit(transferPrice, net);
        const annualBenefit = calculateAnnualBenefit(annualUsage, unitBenefit);

        yearAnnualBenefit += annualBenefit;
      }
    });

    totalWAC += yearWAC;
    totalAnnualBenefit += yearAnnualBenefit;

    yearData.push({
      year: yearLabel,
      revenue: yearRevenue,
      gp: yearGP,
      grossProfit,
      gpPerUnit,
      supplierName: supplierNameValue,
      supplierBulkNdc,
      gsmsNdc,
      annualBenefit: yearAnnualBenefit,
    });
  }

  const totalGrossProfit = calculateGrossProfit(totalRevenue, totalGP);
  const totalGpPerUnit = calculateGPPerUnit(totalGP, totalAnnualUsage);

  return {
    totalRevenue,
    totalGP,
    totalGrossProfit,
    totalGpPerUnit,
    totalWAC,
    totalAnnualBenefit,
    yearData,
  };
}
